import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCsrProfiles, useTechs, useMetros } from '@/hooks/useReferenceData';

export type FollowUpFilter = 'all' | 'overdue' | 'today' | 'this_week' | 'none';

interface LeadsFilterPanelProps {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  onDateChange: (from: Date | undefined, to: Date | undefined) => void;
  selectedCsr: string;
  onCsrChange: (csr: string) => void;
  selectedTech: string;
  onTechChange: (tech: string) => void;
  location: string;
  onLocationChange: (location: string) => void;
  onClearFilters: () => void;
  followUpFilter?: FollowUpFilter;
  onFollowUpFilterChange?: (filter: FollowUpFilter) => void;
  compareEnabled?: boolean;
  onCompareChange?: (enabled: boolean) => void;
}

/** 36px dropdown chip styling shared by the filter selects */
const CHIP_TRIGGER_CLASS =
  'h-9 w-auto gap-1.5 rounded-md bg-background px-3 text-[13px] font-medium';

/**
 * Compact filter chip row for the Leads screen.
 * Renders as a fragment so the parent can lay it out inline with the search input.
 */
export function LeadsFilterPanel(props: LeadsFilterPanelProps) {
  const { data: csrProfiles = [] } = useCsrProfiles();
  const { data: techs = [] } = useTechs();
  const { data: metros = [] } = useMetros(true);

  const hasActiveFilters =
    props.dateFrom !== undefined ||
    props.dateTo !== undefined ||
    props.selectedCsr !== 'all' ||
    props.selectedTech !== 'all' ||
    (!!props.location && props.location !== 'all') ||
    (!!props.followUpFilter && props.followUpFilter !== 'all');

  return (
    <>
      {/* Date chip (popover keeps presets, custom range and the Compare toggle) */}
      <DateRangePicker
        dateFrom={props.dateFrom}
        dateTo={props.dateTo}
        onDateChange={props.onDateChange}
        compareEnabled={props.compareEnabled}
        onCompareChange={props.onCompareChange}
        className="h-9 min-w-0 rounded-md px-3 text-[13px] font-medium"
      />

      {/* CSR chip */}
      <Select value={props.selectedCsr} onValueChange={props.onCsrChange}>
        <SelectTrigger className={CHIP_TRIGGER_CLASS} aria-label="Filter by CSR">
          <span className="text-muted-foreground">CSR</span>
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {csrProfiles.map((profile) => (
            <SelectItem key={profile.id} value={profile.id}>
              {profile.full_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Tech chip */}
      <Select value={props.selectedTech} onValueChange={props.onTechChange}>
        <SelectTrigger className={CHIP_TRIGGER_CLASS} aria-label="Filter by technician">
          <span className="text-muted-foreground">Tech</span>
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="unassigned">Unassigned</SelectItem>
          {techs.map((tech) => (
            <SelectItem key={tech.id} value={tech.id}>
              {tech.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Location chip */}
      <Select value={props.location} onValueChange={props.onLocationChange}>
        <SelectTrigger className={CHIP_TRIGGER_CLASS} aria-label="Filter by location">
          <span className="text-muted-foreground">Location</span>
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {metros.map((metro) => (
            <SelectItem key={metro.id} value={metro.id}>
              {metro.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Follow-up chip */}
      {props.onFollowUpFilterChange && (
        <Select
          value={props.followUpFilter || 'all'}
          onValueChange={(v) => props.onFollowUpFilterChange?.(v as FollowUpFilter)}
        >
          <SelectTrigger className={CHIP_TRIGGER_CLASS} aria-label="Filter by follow-up status">
            <span className="text-muted-foreground">Follow-up</span>
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="today">Due today</SelectItem>
            <SelectItem value="this_week">Due this week</SelectItem>
            <SelectItem value="none">No follow-up set</SelectItem>
          </SelectContent>
        </Select>
      )}

      {/* Clear link */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={props.onClearFilters}
          className="h-9 gap-1 px-2.5 text-[13px] font-medium text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </Button>
      )}
    </>
  );
}
